/** Startseite: a dark hero panel with the viola, an intro section, three
 *  workshop tiles, and a closing invitation. Copy comes from the page's
 *  content blocks (editable in Admin > Texte). */

import { Link } from 'react-router-dom'
import { BlurbCard, OutlineButtonLink } from '../components/ui'
import type { ContentBlock, PageContent } from '../lib/api'
import { mediaSrcSet, mediaUrl } from '../lib/api'
import { useApi, useDocumentMeta, useHreflang, useLocale } from '../lib/hooks'
import { pagePaths, pathFor, switchLocalePath, t } from '../lib/i18n'

const heroButtonClass =
  'group mt-9 inline-flex cursor-pointer items-center gap-3 rounded-[2px] bg-paper px-[24px] py-[14px] ' +
  'font-sans text-[15px] font-semibold leading-none text-ink transition-colors duration-200 hover:bg-amber ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber'

function HeroButtonArrow() {
  return (
    <span aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-[3px]">
      →
    </span>
  )
}

function block(blocks: ContentBlock[], type: ContentBlock['type'], index = 0) {
  return blocks.filter((b) => b.type === type)[index]
}

export default function Home() {
  const locale = useLocale()
  const strings = t(locale)
  const { data } = useApi<PageContent>(`/api/pages/home?locale=${locale}`)

  useDocumentMeta(strings.meta.home.title, strings.meta.home.description)
  useHreflang(pagePaths('home'))

  const blocks = data?.blocks ?? []
  const hero = block(blocks, 'hero')
  // Where the hero button leads - editable in the admin (Texte → Startseite).
  // Stored as the German route; switchLocalePath maps it to the current language.
  const storedTarget = hero?.cta_href || '#content'
  const heroTarget = storedTarget.startsWith('/')
    ? switchLocalePath(storedTarget, locale)
    : storedTarget
  const viola = block(blocks, 'image')
  const heading = block(blocks, 'heading')
  const intro = block(blocks, 'text', 0)
  const tilesLabel = block(blocks, 'text', 1)
  const tiles = blocks.filter((b) => b.type === 'blurb')
  const introParas = (intro?.text ?? '').split('\n').filter(Boolean)

  return (
    <>
      {/* Hero: dark panel, text left, the viola standing free on the right. */}
      <section className="bg-night text-paper" data-testid="hero">
        <div className="mx-auto grid min-h-[min(82vh,760px)] max-w-[1200px] grid-cols-1 items-center gap-10 px-6 pb-16 pt-20 md:px-10 lg:grid-cols-[1.15fr_1fr] lg:pb-0 lg:pt-0">
          <div className="max-w-[600px]">
            {hero?.eyebrow ? (
              <h1 className="eyebrow m-0 !text-amber" data-testid="hero-eyebrow">
                {hero.eyebrow}
              </h1>
            ) : null}
            {hero?.headline ? (
              <p
                className="m-0 mt-5 font-serif text-[clamp(44px,7vw,84px)] font-medium leading-[1.02] text-paper"
                data-testid="hero-headline"
              >
                {hero.headline}
              </p>
            ) : null}
            {hero?.cta ? (
              heroTarget.startsWith('/') ? (
                <Link to={heroTarget} className={heroButtonClass} data-testid="hero-cta">
                  {hero.cta}
                  <HeroButtonArrow />
                </Link>
              ) : (
                <a href={heroTarget} className={heroButtonClass} data-testid="hero-cta">
                  {hero.cta}
                  <HeroButtonArrow />
                </a>
              )
            ) : null}
          </div>

          {viola?.src ? (
            <div className="relative mx-auto w-full max-w-[420px] lg:max-w-none lg:justify-self-end" data-testid="hero-image">
              <img
                src={mediaUrl(viola.src)}
                srcSet={mediaSrcSet(viola.src)}
                sizes="(max-width: 1024px) 80vw, 520px"
                alt={viola.alt || hero?.headline || ''}
                loading="eager"
                decoding="async"
                className="block h-auto w-full max-h-[720px] object-contain drop-shadow-[0_30px_40px_rgba(0,0,0,0.45)]"
              />
            </div>
          ) : null}
        </div>
      </section>

      {/* Intro: heading left, paragraphs right. */}
      <section id="content" className="bg-paper px-6 py-16 md:px-10 md:py-24" data-testid="intro-section">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20">
          <div>
            {heading?.text ? (
              <h2 className="rule m-0 font-serif text-[clamp(32px,4vw,46px)] font-medium leading-[1.1] text-ink">
                {heading.text}
              </h2>
            ) : null}
          </div>
          <div className="font-sans text-[19px] leading-[1.65] text-ink">
            {introParas.map((line, index) => (
              <p key={index} className="m-0 mb-[1.1em] last:mb-0">
                {line}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Workshop tiles on the deeper paper tone. */}
      <section className="bg-paper-deep px-6 py-16 md:px-10 md:py-24" data-testid="categories">
        <div className="mx-auto max-w-[1200px]">
          {tilesLabel?.text ? (
            <h3 className="rule m-0 mb-12 font-serif text-[clamp(30px,3.6vw,40px)] font-medium leading-[1.1] text-ink">
              {tilesLabel.text.split('\n')[0]}
            </h3>
          ) : null}
          <div className="grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-3">
            {tiles.map((tile, index) => (
              <BlurbCard
                key={index}
                filename={tile.image ?? ''}
                alt={tile.alt ?? ''}
                title={tile.title ?? ''}
                description={tile.description}
                to={pathFor(locale, 'shop')}
                eager={index === 0}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Closing invitation. */}
      <section className="bg-paper px-6 py-16 md:px-10 md:py-20" data-testid="home-invite">
        <div className="mx-auto flex max-w-[1200px] flex-col items-start gap-6 border-t border-line pt-12 md:flex-row md:items-center md:justify-between">
          <p className="m-0 max-w-[36ch] font-serif text-[clamp(26px,3vw,34px)] leading-[1.2] text-ink">
            {strings.home.invite}
          </p>
          <OutlineButtonLink to={pathFor(locale, 'kontakt')}>{strings.nav.kontakt}</OutlineButtonLink>
        </div>
      </section>
    </>
  )
}
